import 'moment/locale/zh-tw';

import { NotificationTwoTone } from '@ant-design/icons';
import { DatePicker, Form, Table } from 'antd';
import moment, { type Moment } from 'moment';
import React, { useMemo } from 'react';

import sharedStyles from '../../sharedStyles.module.scss';
import type {
  CleanReservationDetailsResponse,
  CleanReserveList,
} from '../../sharedTypes';
import { getReservationDateStatus, getTicketId } from '../helper';
import type { ReservationDateStatus } from '../types';
import { checkIsDisabledDate, getQuantity, getTableColumns } from './helper';
import styles from './index.module.scss';

moment.locale('zh-tw', { week: { dow: 7 } });

type OrderContentProps = {
  reservationList: CleanReserveList | undefined;
  isShowQuantityError: boolean;
  hideInputNumberErrorMsg: () => void;
  editingReservation?: CleanReserveList['clean'][0] & { order: number };
  reservationDatesStatusState: {
    data: ReservationDateStatus['days'];
    setState: React.Dispatch<
      React.SetStateAction<ReservationDateStatus['days']>
    >;
  };
  cleanKindInformation: CleanReservationDetailsResponse | undefined;
};

const { Item } = Form;

const OrderContent = ({
  reservationList,
  isShowQuantityError,
  hideInputNumberErrorMsg,
  editingReservation,
  reservationDatesStatusState: {
    data: reservationDatesStatus,
    setState: updateReservationDatesStatus,
  },
  cleanKindInformation,
}: OrderContentProps) => {
  const dataSource = useMemo(() => {
    const data = cleanKindInformation?.cleanServiceItems.map((item) => {
      if (item.priceInfo.kind === 'Free') {
        return {
          ...item,
          quantity: 1,
        };
      }
      if (editingReservation?.items && editingReservation?.items.length > 0) {
        const quantity = getQuantity(item, editingReservation?.items);

        const newItem = {
          ...item,
          quantity,
        };
        return newItem;
      }
      return {
        ...item,
        quantity: null,
      };
    });

    return data;
  }, [cleanKindInformation?.cleanServiceItems, editingReservation?.items]);

  const tableColumns = getTableColumns({
    isShowQuantityError,
    hideInputNumberErrorMsg,
  });

  const fetchNewReservationDateStatus = async (nextMonthDate: Moment) => {
    const ticketId = getTicketId(cleanKindInformation?.cleanServiceItems);
    if (cleanKindInformation && reservationList && ticketId) {
      const selectedDates = reservationList.clean.map((reservation) =>
        reservation.date.valueOf()
      );
      const newReservationDatesStatus = await getReservationDateStatus({
        cleanKindId: cleanKindInformation._id,
        month: nextMonthDate.valueOf(),
        communityName: reservationList.communityName,
        householdNumber: reservationList.householdNumber,
        userId: reservationList.userId,
        ticketId,
        selectedDates,
      });

      updateReservationDatesStatus(newReservationDatesStatus.days);
    }
  };

  const reservationRule = {
    memberReservationLimit: cleanKindInformation?.memberReservationLimit,
    canReserveAfter: cleanKindInformation?.cleanReservationRule2Start,
    canReserveBefore: cleanKindInformation?.cleanReservationRule2End,
  };

  return (
    <>
      <div className={styles.content}>
        <div>
          <p>
            <NotificationTwoTone className={sharedStyles.noticeIcon} />
            清潔項目：
          </p>
        </div>
        <div>
          <Table
            className={styles.cleanItem}
            pagination={false}
            bordered={false}
            columns={tableColumns}
            dataSource={dataSource}
            tableLayout="auto"
            scroll={{ x: 'max-content' }}
            rowKey="_id"
          />
        </div>
      </div>
      <div className={styles.content}>
        <Item
          label="預約日期"
          name="date"
          rules={[{ required: true, message: '請選擇預約日期' }]}
          initialValue={editingReservation?.date}
        >
          <DatePicker
            disabledDate={(current) =>
              checkIsDisabledDate({
                checkDate: current,
                reservationRule,
                reservationList: reservationList?.clean,
                editingDate: editingReservation?.date,
                reservationDatesStatus,
              })
            }
            format="YYYY/MM/DD"
            inputReadOnly
            onPanelChange={fetchNewReservationDateStatus}
          />
        </Item>
      </div>
    </>
  );
};

export default OrderContent;
